import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { pageFields } from './page-fields.js'
import { adminTaxCountrySchema } from './tax-configs.screen.js'
import { adminPaginationSchema } from './users.screen.js'

/*
 * «Plantillas PDF»: the list (`admin.pdfTemplates`: one page of templates with the filters of the table, the options of
 * those filters, the coverage catalogue and the countries that have a tax configuration) and the editor
 * (`admin.pdfTemplates.form`: the template being edited, or none when creating, and the same countries).
 *
 * Each row carries ONLY what its page paints or edits: the list has neither the HTML nor the CSS nor the style
 * configuration of a template (a template weighs ≈ 170 KB with them, and the list used to download all of them to paint
 * ≈ 3 KB), nor the tenant, the authorship or the dates of change; the coverage row has only what the coverage notice
 * counts. Strict: a field that is not named here does not travel.
 */

/** The statuses the filter of the list offers; «draft» wins over «active» (a draft is never active), as in the table. */
export const PDF_TEMPLATE_STATUS_FILTERS = ['active', 'inactive', 'draft'] as const

const colorsSchema = z.strictObject({
  primary: z.string(),
  secondary: z.string(),
  text: z.string(),
  background: z.string(),
  accent: z.string(),
  border: z.string(),
  headerBg: z.string(),
  footerBg: z.string(),
  subtotalBg: z.string(),
})

const typographySchema = z.strictObject({
  fontFamily: z.string(),
  h1Size: z.number(),
  h2Size: z.number(),
  h3Size: z.number(),
  h4Size: z.number(),
  bodySize: z.number(),
  smallSize: z.number(),
})

const logosSchema = z.strictObject({
  leftLogoUrl: z.string().nullable(),
  rightLogoUrl: z.string().nullable(),
  leftLogoWidth: z.number(),
  rightLogoWidth: z.number(),
})

const spacingSchema = z.strictObject({
  pageMarginTop: z.number(),
  pageMarginRight: z.number(),
  pageMarginBottom: z.number(),
  pageMarginLeft: z.number(),
  sectionSpacing: z.number(),
  paragraphSpacing: z.number(),
})

const layoutSchema = z.strictObject({
  pageSize: z.string(),
  pageOrientation: z.string(),
})

/** The 28 tokens of `PdfStyleConfig`, written one by one: a token added there without being added here fails the type assertions. */
export const pdfStyleConfigSchema = z.strictObject({
  colors: colorsSchema,
  typography: typographySchema,
  logos: logosSchema,
  spacing: spacingSchema,
  layout: layoutSchema,
})

/** The template the editor edits and sends back: everything the form holds, nothing of the authorship. */
export const adminPdfTemplateEditableSchema = z.strictObject({
  id: z.guid(),
  name: z.string(),
  description: z.string().nullable(),
  countries: z.array(z.string()),
  reportTypes: z.array(z.string()),
  htmlTemplate: z.string(),
  cssTemplate: z.string(),
  styleConfig: pdfStyleConfigSchema,
  isActive: z.boolean(),
  isDraft: z.boolean(),
})

/**
 * A row of the list: what the table paints (name, version, countries, report types, status, date of creation). It
 * shares its columns with the editable template, so a column that changes there changes here.
 */
export const adminPdfTemplateRowSchema = adminPdfTemplateEditableSchema
  .pick({
    id: true,
    name: true,
    description: true,
    countries: true,
    reportTypes: true,
    isActive: true,
    isDraft: true,
  })
  .extend({ version: z.string(), createdAt: z.iso.datetime() })

/** What the coverage notice counts: which countries have a template, and whether it is global and in use. */
export const adminPdfTemplateCoverageRowSchema = z.strictObject({
  id: z.guid(),
  countries: z.array(z.string()),
  isActive: z.boolean(),
  isDraft: z.boolean(),
  isGlobal: z.boolean(),
})

export const adminPdfTemplateFilterOptionsSchema = z.strictObject({
  countries: z.array(z.string()),
  reportTypes: z.array(z.string()),
})

export const adminPdfTemplatesQuerySchema = z.strictObject({
  ...pageFields(20),
  search: z.string().trim().min(1).max(200).optional(),
  country: z.string().min(1).max(64).optional(),
  reportType: z.string().min(1).max(64).optional(),
  status: z.enum(PDF_TEMPLATE_STATUS_FILTERS).optional(),
})

export const adminPdfTemplatesScreen = defineScreen({
  id: 'admin.pdfTemplates',
  path: '/api/screens/admin/pdf-templates',
  access: 'super_admin',
  query: adminPdfTemplatesQuerySchema,
  response: z.strictObject({
    page: screenSection(
      z.strictObject({
        items: z.array(adminPdfTemplateRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    filterOptions: screenSection(adminPdfTemplateFilterOptionsSchema),
    coverage: screenSection(
      z.strictObject({
        rows: z.array(adminPdfTemplateCoverageRowSchema),
        total: z.number().int().min(0),
      }),
    ),
    countries: screenSection(z.array(adminTaxCountrySchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.pdfTemplates'],
  budget: { requests: 1, statements: 22, bytes: 40_000, p95Ms: 300 },
  summary:
    'PDF templates: one page of templates with the filters of the list, the filter options, the coverage catalogue and the countries with a tax configuration',
})

export const adminPdfTemplateFormScreen = defineScreen({
  id: 'admin.pdfTemplates.form',
  path: '/api/screens/admin/pdf-templates/form',
  access: 'super_admin',
  query: z.strictObject({ id: z.guid().optional() }),
  response: z.strictObject({
    /** The subject: `null` when creating; a template that does not exist is the 404 of the WHOLE screen. */
    template: adminPdfTemplateEditableSchema.nullable(),
    countries: screenSection(z.array(adminTaxCountrySchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.pdfTemplates'],
  budget: { requests: 1, statements: 9, bytes: 150_000, p95Ms: 250 },
  summary:
    'PDF templates › form: the template being edited (none when creating) and the countries with a tax configuration',
})

export type PdfTemplateStatusFilter = (typeof PDF_TEMPLATE_STATUS_FILTERS)[number]
export type AdminPdfTemplateRow = z.infer<typeof adminPdfTemplateRowSchema>
export type AdminPdfTemplateCoverageRow = z.infer<typeof adminPdfTemplateCoverageRowSchema>
export type AdminPdfTemplateEditable = z.infer<typeof adminPdfTemplateEditableSchema>
export type AdminPdfTemplatesQuery = ScreenQuery<typeof adminPdfTemplatesScreen>
export type AdminPdfTemplatesData = ScreenData<typeof adminPdfTemplatesScreen>
export type AdminPdfTemplateFormData = ScreenData<typeof adminPdfTemplateFormScreen>
